if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
      }

      hide() {
        super.hide();
        // Restablece el zoom al cerrar el modal
        if (this.lgResetZoom) this.lgResetZoom();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
        this.setupZoomAndPan();
      }

      showActiveMedia() {
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }

      // Zoom y desplazamiento por delegación: product-info.js sustituye el HTML del
      // modal al cambiar de variante, así que no se pueden enlazar las <img> una a una.
      setupZoomAndPan() {
        if (this.zoomAndPanInitialized) return;
        this.zoomAndPanInitialized = true;

        const MAX_SCALE = 5;
        const DOUBLE_TAP_SCALE = 2.5;
        const SELECTOR = '.product-media-modal__content img';

        const states = new WeakMap();
        const pointers = new Map();
        let img = null;
        let gesture = null;
        let moved = false;
        let lastTap = { time: 0, x: 0, y: 0 };

        const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
        const st = (el) => {
          let s = states.get(el);
          if (!s) { s = { scale: 1, tx: 0, ty: 0 }; states.set(el, s); }
          return s;
        };

        // Rango de desplazamiento en un eje según el tamaño REAL de la imagen ampliada
        // frente al área visible: si es mayor que la pantalla se puede recorrer entera
        // (también sin zoom: en móvil el tema ya la muestra más grande que la pantalla);
        // si es menor, solo puede acercarse al centro.
        const axisRange = (center, size, viewStart, viewEnd) => {
          const half = size / 2;
          if (size >= viewEnd - viewStart) {
            return [viewEnd - half - center, viewStart + half - center];
          }
          const d = (viewStart + viewEnd) / 2 - center;
          return [Math.min(0, d), Math.max(0, d)];
        };

        const apply = (el, animate) => {
          const s = st(el);
          const view = (el.closest('.product-media-modal__content') || this).getBoundingClientRect();
          const c = baseCenter(el);
          const [minX, maxX] = axisRange(c.x, el.offsetWidth * s.scale, view.left, view.right);
          const [minY, maxY] = axisRange(c.y, el.offsetHeight * s.scale, view.top, view.bottom);
          s.tx = clamp(s.tx, minX, maxX);
          s.ty = clamp(s.ty, minY, maxY);
          el.style.transition = animate ? 'transform 0.25s ease-out' : 'none';
          el.style.setProperty('--zoom', s.scale);
          el.style.setProperty('--pan-x', `${s.tx}px`);
          el.style.setProperty('--pan-y', `${s.ty}px`);
          el.isZoomed = s.scale > 1.001;
          el.classList.toggle('is-zoomed', el.isZoomed);
          this.classList.toggle('lg-zoomed', el.isZoomed);
        };

        // Centro de la imagen sin transformar (coordenadas de pantalla)
        const baseCenter = (el) => {
          const s = st(el);
          const r = el.getBoundingClientRect();
          return { x: r.left + r.width / 2 - s.tx, y: r.top + r.height / 2 - s.ty };
        };

        // Cambia la escala manteniendo fijo el punto (mx, my) de la pantalla
        const zoomAt = (el, newScale, mx, my, animate) => {
          const s = st(el);
          const c = baseCenter(el);
          const ux = (mx - c.x - s.tx) / s.scale;
          const uy = (my - c.y - s.ty) / s.scale;
          s.scale = clamp(newScale, 1, MAX_SCALE);
          s.tx = mx - c.x - s.scale * ux;
          s.ty = my - c.y - s.scale * uy;
          apply(el, animate);
        };

        const toggleZoomAt = (el, x, y) => {
          const s = st(el);
          if (s.scale > 1.001) {
            s.scale = 1; s.tx = 0; s.ty = 0;
            apply(el, true);
          } else {
            zoomAt(el, DOUBLE_TAP_SCALE, x, y, true);
          }
        };

        const startGesture = () => {
          const pts = [...pointers.values()];
          if (!img || !pts.length) { gesture = null; return; }
          const s = st(img);
          if (pts.length >= 2) {
            const [p1, p2] = pts;
            gesture = {
              type: 'pinch', c: baseCenter(img), s0: s.scale, t0x: s.tx, t0y: s.ty,
              dist: Math.hypot(p2.x - p1.x, p2.y - p1.y) || 1,
              mx: (p1.x + p2.x) / 2, my: (p1.y + p2.y) / 2
            };
          } else {
            gesture = { type: 'pan', sx: pts[0].x - s.tx, sy: pts[0].y - s.ty };
          }
        };

        this.addEventListener('pointerdown', (e) => {
          const target = e.target.closest && e.target.closest(SELECTOR);
          if (!target) return;
          if (e.pointerType === 'mouse' && e.button !== 0) return;
          if (img && target !== img) { pointers.clear(); }
          img = target;
          pointers.set(e.pointerId, { x: e.clientX, y: e.clientY, sx: e.clientX, sy: e.clientY });
          try { img.setPointerCapture(e.pointerId); } catch (err) { /* sin captura */ }
          if (pointers.size === 1) moved = false;
          startGesture();
        });

        this.addEventListener('pointermove', (e) => {
          const p = pointers.get(e.pointerId);
          if (!p || !gesture || !img || !img.isConnected) return;
          p.x = e.clientX;
          p.y = e.clientY;
          if (Math.hypot(p.x - p.sx, p.y - p.sy) > 6) moved = true;
          const s = st(img);

          if (gesture.type === 'pinch' && pointers.size >= 2) {
            const [p1, p2] = [...pointers.values()];
            const dist = Math.hypot(p2.x - p1.x, p2.y - p1.y);
            const mx = (p1.x + p2.x) / 2;
            const my = (p1.y + p2.y) / 2;
            // El punto bajo los dedos al empezar sigue al punto medio actual
            const ns = clamp(gesture.s0 * (dist / gesture.dist), 1, MAX_SCALE);
            const ux = (gesture.mx - gesture.c.x - gesture.t0x) / gesture.s0;
            const uy = (gesture.my - gesture.c.y - gesture.t0y) / gesture.s0;
            s.scale = ns;
            s.tx = mx - gesture.c.x - ns * ux;
            s.ty = my - gesture.c.y - ns * uy;
            apply(img, false);
          } else if (gesture.type === 'pan' && (s.scale > 1.001 || e.pointerType !== 'mouse')) {
            s.tx = p.x - gesture.sx;
            s.ty = p.y - gesture.sy;
            apply(img, false);
          }
        });

        const endPointer = (e) => {
          const p = pointers.get(e.pointerId);
          if (!p || !img) return;
          pointers.delete(e.pointerId);
          try { img.releasePointerCapture(e.pointerId); } catch (err) { /* sin captura */ }

          if (e.type === 'pointerup' && !moved && pointers.size === 0) {
            if (e.pointerType === 'mouse') {
              toggleZoomAt(img, e.clientX, e.clientY);
            } else {
              // Táctil: doble toque = zoom / reset
              const now = Date.now();
              const near = Math.hypot(e.clientX - lastTap.x, e.clientY - lastTap.y) < 40;
              if (now - lastTap.time < 320 && near) {
                toggleZoomAt(img, e.clientX, e.clientY);
                lastTap = { time: 0, x: 0, y: 0 };
              } else {
                lastTap = { time: now, x: e.clientX, y: e.clientY };
              }
            }
          }
          if (pointers.size === 0) apply(img, true);
          startGesture();
          if (pointers.size === 1) {
            // Al soltar un dedo del pellizco, se sigue arrastrando con el otro
            const rest = [...pointers.values()][0];
            rest.sx = rest.x; rest.sy = rest.y;
            moved = true;
          }
        };
        this.addEventListener('pointerup', (e) => {
          // El tema cierra el modal con cualquier pointerup de ratón: sobre la imagen no
          if (e.pointerType === 'mouse' && e.target.closest && e.target.closest(SELECTOR)) e.stopPropagation();
          endPointer(e);
        }, true);
        this.addEventListener('pointercancel', endPointer);

        // Rueda del ratón / trackpad: zoom hacia el cursor
        this.addEventListener('wheel', (e) => {
          const target = e.target.closest && e.target.closest(SELECTOR);
          if (!target) return;
          if (!e.ctrlKey && st(target).scale <= 1.001) return;
          e.preventDefault();
          zoomAt(target, st(target).scale * Math.exp(-e.deltaY * 0.0015), e.clientX, e.clientY, false);
        }, { passive: false });

        this.addEventListener('dragstart', (e) => {
          if (e.target.closest && e.target.closest(SELECTOR)) e.preventDefault();
        });

        this.lgResetZoom = () => {
          pointers.clear();
          gesture = null;
          this.querySelectorAll(SELECTOR).forEach((el) => {
            states.delete(el);
            el.style.removeProperty('--zoom');
            el.style.removeProperty('--pan-x');
            el.style.removeProperty('--pan-y');
            el.style.transition = '';
            el.isZoomed = false;
            el.classList.remove('is-zoomed');
          });
          this.classList.remove('lg-zoomed');
        };
      }
    }
  );
}
